/**
 * FactionPanel — Displays current faction reputations with color-coded bars.
 * Accessible via window.__debug.openFactions() or the F key in-game.
 *
 * Each row shows: faction icon, name, reputation bar (-100 to 100 scale), standing label.
 * Colors: red (Hostile), orange (Unfriendly), gray (Neutral), green (Friendly), gold (Honored).
 *
 * @module FactionPanel
 */

import { UIPanel } from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import FactionSystem from '../systems/FactionSystem.js';

// ─── Standing color map ───────────────────────────────────────────────────────

const STANDING_COLORS = {
  Hostile:    '#e53935',
  Unfriendly: '#fb8c00',
  Neutral:    '#9e9e9e',
  Friendly:   '#43a047',
  Honored:    '#fdd835',
};

// ─── FactionPanel ─────────────────────────────────────────────────────────────

class FactionPanel extends UIPanel {
  constructor() {
    super('panel-factions', {
      trapFocus: false,
      zLayer:    'panel',
    });

    /** @type {Function|null} EventBus unsub for faction:repChanged */
    this._repUnsub = null;

    /** @type {Function|null} keydown listener for F key */
    this._keyFn = null;

    this._injectStyles();
    this._attachKeyListener();

    // Register on window for debug access
    window.__factionPanel = this;

    DebugLog.info('[faction] FactionPanel initialized');
  }

  // ─── UIPanel overrides ────────────────────────────────────────────────────

  _buildHTML() {
    return `
      <div class="fac-panel-inner">
        <div class="fac-header">
          <span class="fac-title">Faction Reputations</span>
          <button class="fac-close-btn" aria-label="Close factions panel">&#x2715;</button>
        </div>
        <div class="fac-list" id="fac-list-rows"></div>
      </div>
    `;
  }

  /**
   * Called by UIPanel.show() — refresh the reputation rows whenever the panel opens.
   */
  updateContent() {
    this._renderRows();

    // Subscribe to live rep changes so bars update while panel is open
    if (!this._repUnsub) {
      this._repUnsub = EventBus.on('faction:repChanged', () => {
        if (this._visible) this._renderRows();
      });
    }

    // Wire close button
    const closeBtn = this.el.querySelector('.fac-close-btn');
    if (closeBtn) {
      closeBtn.onclick = () => this.hide();
    }
  }

  hide() {
    super.hide();

    // Unsubscribe from live updates when panel is closed
    if (this._repUnsub) {
      this._repUnsub();
      this._repUnsub = null;
    }
  }

  // ─── Rendering ────────────────────────────────────────────────────────────

  /**
   * Rebuild all faction rows from current FactionSystem state.
   */
  _renderRows() {
    const container = this.el.querySelector('#fac-list-rows');
    if (!container) return;

    const factions = FactionSystem.getAllFactions();

    container.innerHTML = factions.map(faction => {
      const { reputation, standing } = faction;
      const color = STANDING_COLORS[standing.label] ?? '#9e9e9e';

      // Map reputation (-100..100) to a percentage (0..100) for the bar fill
      const barPct = ((reputation + 100) / 200) * 100;

      // The "zero line" sits at 50% of the track width
      const isPositive = reputation >= 0;
      const fillPct    = Math.abs(reputation) / 2; // 0–50% width for each half

      return `
        <div class="fac-row" data-faction-id="${faction.id}">
          <div class="fac-icon" aria-hidden="true">${faction.icon}</div>
          <div class="fac-info">
            <div class="fac-name">${faction.name}</div>
            <div class="fac-bar-track" title="${reputation} / 100">
              <!-- Left half: negative reputation -->
              <div class="fac-bar-half fac-bar-left">
                ${!isPositive
                  ? `<div class="fac-bar-fill fac-fill-neg" style="width:${fillPct}%; background:${color};"></div>`
                  : ''}
              </div>
              <!-- Center divider -->
              <div class="fac-bar-center"></div>
              <!-- Right half: positive reputation -->
              <div class="fac-bar-half fac-bar-right">
                ${isPositive
                  ? `<div class="fac-bar-fill fac-fill-pos" style="width:${fillPct}%; background:${color};"></div>`
                  : ''}
              </div>
            </div>
            <div class="fac-standing" style="color:${color};">${standing.label}</div>
          </div>
          <div class="fac-rep-val">${reputation > 0 ? '+' : ''}${reputation}</div>
        </div>
      `;
    }).join('');
  }

  // ─── F key listener ───────────────────────────────────────────────────────

  _attachKeyListener() {
    this._keyFn = (e) => {
      // Only trigger when no input is focused and no modal is open
      if (
        e.key === 'f' || e.key === 'F'
      ) {
        const active = document.activeElement;
        const isInput = active && (
          active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.tagName === 'SELECT' ||
          active.isContentEditable
        );
        if (isInput) return;

        this.toggle();
      }
    };

    window.addEventListener('keydown', this._keyFn);
  }

  // ─── Styles ───────────────────────────────────────────────────────────────

  _injectStyles() {
    if (document.getElementById('faction-panel-styles')) return;

    const style = document.createElement('style');
    style.id = 'faction-panel-styles';
    style.textContent = FACTION_PANEL_STYLES;
    document.head.appendChild(style);
  }
}

// ─── Component CSS ────────────────────────────────────────────────────────────

const FACTION_PANEL_STYLES = `
/* ── Faction Panel ──────────────────────────────────────────────────────── */

#panel-factions {
  display: none;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: clamp(320px, 50vw, 560px);
  max-height: 80vh;
  background: rgba(10, 7, 3, 0.96);
  border: 1px solid rgba(90, 74, 32, 0.7);
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.7);
  font-family: 'Georgia', 'Times New Roman', serif;
  color: #d0b870;
  overflow: hidden;
  flex-direction: column;
}

#panel-factions.visible {
  display: flex;
}

.fac-panel-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
}

/* Header */
.fac-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 10px;
  border-bottom: 1px solid rgba(90, 74, 32, 0.4);
  flex-shrink: 0;
}

.fac-title {
  font-size: clamp(14px, 2vw, 18px);
  font-weight: bold;
  letter-spacing: 0.06em;
  color: #e8c860;
}

.fac-close-btn {
  background: none;
  border: 1px solid rgba(90, 74, 32, 0.5);
  color: #a08040;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 7px;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s;
}

.fac-close-btn:hover,
.fac-close-btn:focus {
  background: rgba(90, 74, 32, 0.3);
  color: #e8c860;
  outline: none;
}

/* Faction list */
.fac-list {
  overflow-y: auto;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fac-list::-webkit-scrollbar {
  width: 5px;
}
.fac-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
}
.fac-list::-webkit-scrollbar-thumb {
  background: rgba(90, 74, 32, 0.5);
  border-radius: 3px;
}

/* Individual faction row */
.fac-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fac-icon {
  font-size: 22px;
  flex-shrink: 0;
  width: 28px;
  text-align: center;
  line-height: 1;
}

.fac-info {
  flex: 1;
  min-width: 0;
}

.fac-name {
  font-size: clamp(11px, 1.3vw, 13px);
  color: #c8a840;
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Rep bar — split left (negative) / right (positive) around a center line */
.fac-bar-track {
  display: flex;
  align-items: center;
  height: 6px;
  gap: 0;
  margin-bottom: 3px;
}

.fac-bar-half {
  flex: 1;
  height: 100%;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.fac-bar-left {
  border-radius: 3px 0 0 3px;
  display: flex;
  justify-content: flex-end;
}

.fac-bar-right {
  border-radius: 0 3px 3px 0;
  display: flex;
  justify-content: flex-start;
}

.fac-bar-fill {
  height: 100%;
  transition: width 0.4s ease;
}

.fac-bar-center {
  width: 2px;
  height: 10px;
  background: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
}

.fac-standing {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: bold;
}

/* Rep numeric value */
.fac-rep-val {
  font-size: 12px;
  color: #a08040;
  min-width: 36px;
  text-align: right;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 480px) {
  #panel-factions {
    width: 96vw;
    top: 5vh;
    left: 2vw;
    transform: none;
    max-height: 90vh;
  }
}
`;

// ─── Singleton export ─────────────────────────────────────────────────────────

export default new FactionPanel();
